<template>
  <!-- Renderless 组件：仅管理预标注地图图层，无 DOM 输出 -->
  <teleport to="#home-body" v-if="preAnnotationList && preAnnotationList.length > 0">
    <div class="tree-exposure-count-list">
      <div class="exposure-count-item exposure-count-title">
        <div>区域名称</div>
        <div>事件数</div>
      </div>
      <el-scrollbar max-height="20rem">
        <div class="exposure-count-item" v-for="(exposure, index) of preAnnotationList" :key="index"
          @click="preAnnotationListClick(exposure)">
          <div class="ellipsis">{{ (exposure['data'] || {})['annotationName'] }}</div>
          <div>{{ ((exposure['data'] || {})['eventList'] || []).length }}</div>
        </div>
      </el-scrollbar>
    </div>
  </teleport>

  <teleport to="#home-body">
    <EditPopup class="manage-panel-exposure-record-Charts" v-if="frequencyChart.data" :title="frequencyChart.title"
      :controls="frequencyChart.controls">
      <FrequencyChart :showFlag="true" :dataList="frequencyChart.data" ref="frequencyChartRef"></FrequencyChart>
    </EditPopup>
  </teleport>

  <teleport to="#home-body" v-if="eventTarget">
    <EventInfo :target="eventTarget" @close="eventTarget = undefined"></EventInfo>
  </teleport>
</template>

<script setup>
import {
  onMounted, onUnmounted,
  computed, ref, reactive, toRaw,
} from 'vue';
import store from '@/store';
import { queryAnnotation } from '@/api/UserWorkspaceDataApi';
import PreAnnotationUse from '@/views/Home/component/ManagePanel/components/SelectedTreeTable/utils/PreAnnotationUse';
import { destroyAllPopups } from '@/views/Home/component/ManagePanel/components/SelectedTreeTable/utils/usePolyInfoPanel';
import FrequencyChart from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/CustomLocaleControl/components/Frequency/components/FrequencyChart';
import EditPopup from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/CustomLocaleControl/components/EditPopup';
import EventInfo from '@/views/Home/component/ManagePanel/components/SelectedTreeTable/components/EventInfo';

const xlsx = require('xlsx');

const props = defineProps({
  mapId: { type: [String, Number], required: true },
});

const homeMap = computed(() => store.getters['getHomeMap']),
  map = toRaw(homeMap.value),
  layerGroup = L.featureGroup().addTo(map);

// 预标注 zoom 回调管理
const preAnnotationMapZoomMap = new Map();
const zoomCallBack = (lg, warningMarkerList, infoLayerGroup) => {
  const zoomHandler = (zoom) => {
    if (zoom >= 6) {
      warningMarkerList.forEach(marker => lg.removeLayer(marker));
      lg.addLayer(infoLayerGroup);
      return;
    }
    if (zoom <= 4) {
      warningMarkerList.forEach(marker => lg.addLayer(marker));
      lg.removeLayer(infoLayerGroup);
    }
  };
  preAnnotationMapZoomMap.set(lg, zoomHandler);
  lg.on({
    remove: () => preAnnotationMapZoomMap.delete(lg),
    add: () => preAnnotationMapZoomMap.set(lg, zoomHandler),
  });
};

const mapZoomendFn = (e) => {
  const zoom = e.target.getZoom();
  preAnnotationMapZoomMap.forEach((handler) => handler && handler(zoom));
};

// 数据管理
const preAnnotationMap = ref(new Map());
const preAnnotationMapSet = (id, group) => {
  preAnnotationMapDel(id);
  group && layerGroup.addLayer(group);
  const m = preAnnotationMap.value;
  m.set(id, group);
  preAnnotationMap.value = new Map(m);
};
const preAnnotationMapDel = (id) => {
  const m = preAnnotationMap.value;
  if (!m.has(id)) return;
  const group = m.get(id);
  group && layerGroup.removeLayer(group);
  m.delete(id);
  preAnnotationMap.value = new Map(m);
};

const preAnnotationList = computed(() => {
  const valList = Array.from(preAnnotationMap.value.values());
  return valList.map(group => {
    const dataLayerList = group && group.options && group.options['dataLayerList'];
    return dataLayerList || [];
  }).flat();
});

const preAnnotationListClick = (target) => {
  const { layer } = target;
  const bounds = layer && layer.getBounds();
  bounds && bounds.isValid() && map.fitBounds(bounds, { maxZoom: 7 });
};

// 事件/频次图表
const eventTarget = ref(undefined);
const eventTargetChange = (target) => { eventTarget.value = target; };

const frequencyChartRef = ref();
const chartDownloadFn = () => {
  frequencyChartRef.value?.chartDownloadFn?.('频次图表');
};

const downloadList = [
  { name: '日期', prop: 'exposureTime' },
  { name: '次数', prop: 'count' },
];
const excelHeader = downloadList.map(e => e.name);
const downloadExposureFn = () => {
  const data = [
    excelHeader,
    ...(frequencyChart.data.map(item => downloadList.map(e => item[e.prop])))
  ];
  const ws = xlsx.utils.aoa_to_sheet(data);
  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
  xlsx.writeFile(wb, 'download.xlsx');
};

const frequencyChartInitFn = (item) => {
  if ((item['exposureCountList'] || []).length == 0) return;
  frequencyChart.title = `${item['annotationName']}线索区频次`;
  frequencyChart.data = item['exposureCountList'];
};

const frequencyChart = reactive({
  title: '频次',
  data: undefined,
  controls: [
    { iconName: 'download-center', fill: 'rgb(102, 203, 118)', title: '数据导出', onClick: downloadExposureFn },
    { iconName: 'image', fill: 'rgb(102, 203, 118)', onClick: chartDownloadFn },
    { iconName: 'close-box', fill: 'rgb(6, 180, 221)', onClick: () => { frequencyChart.data = undefined } },
  ]
});

// 请求数据
const queryAnnotationFn = (id) => {
  queryAnnotation(`1/${id}`).then(res => {
    if (res.data.code == 1004) {
      const group = PreAnnotationUse(
        res.data.dataList || [],
        frequencyChartInitFn,
        eventTargetChange,
        zoomCallBack,
        map,
      );
      preAnnotationMapSet(id, group);
    }
  }).catch(err => {
    console.log(err);
  });
};

onMounted(() => {
  map.on('zoomend', mapZoomendFn);
  queryAnnotationFn(props.mapId);
});

onUnmounted(() => {
  map.off('zoomend', mapZoomendFn);
  // 彻底销毁所有 dragPopup 弹窗的 Vue 实例
  destroyAllPopups();
  // 清除所有预标注图层
  preAnnotationMap.value.forEach((group) => {
    group && layerGroup.removeLayer(group);
  });
  map.removeLayer(layerGroup);
});
</script>

<style lang='less'>
/* 地图预标注警告标识动画 */
.pre-annotation-warning {
  img {
    width: 100%;
    height: 100%;
    animation: heartBeat 2s infinite;
  }
}

.tree-exposure-count-list {
  position: absolute;
  bottom: 80px;
  right: 10px;
  border-radius: var(--borderRadiusSize_2);
  box-shadow: var(--boxShadow);
  background: var(--backgroundColor_2);
  border: 1px solid var(--borderColor_9);
  padding: 10px;
  backdrop-filter: blur(15px);
  width: 240px;
  max-height: 400px;

  .el-scrollbar {
    .el-scrollbar__view {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
  }

  .exposure-count-item {
    display: flex;
    cursor: pointer;
    border-radius: var(--borderRadiusSize);
    padding: 5px;

    &:hover {
      background-color: var(--backgroundColor_2);
    }

    div {
      font-size: 16px;
      font-family: OPPOSans-B;

      &:nth-child(1) {
        color: var(--fontColor_5);
        text-align: left;
        flex: 1;
        min-width: 0;
      }

      &:nth-child(2),
      &:nth-child(3) {
        color: var(--fontColor_12);
        width: 60px;
        text-align: center;
      }
    }
  }

  .exposure-count-title {
    &:hover {
      background-color: transparent;
    }

    div {
      font-size: 14px;
      font-family: OPPOSans;

      &:nth-child(1) {
        color: var(--fontColor_10);
        text-align: left;
        flex: 1;
        min-width: 0;
      }

      &:nth-child(2),
      &:nth-child(3) {
        color: var(--fontColor_10);
        text-align: center;
      }
    }
  }
}
</style>